import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Mail, Phone, ExternalLink, Send, ClipboardCheck, Settings } from "lucide-react";
import ManageOnboardedProjectModal from "./ManageOnboardedProjectModal";
import TeamSummaryChips, { useProjectTeams } from "@/components/shared/TeamSummaryChips";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";

export default function CrmOnboardingPage() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [managingId, setManagingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes, iRes] = await Promise.all([
        api.get("/crm/design-projects").catch(() => ({ data: [] })),
        api.get("/crm/clients"),
        api.get("/crm/onboarding-invites"),
      ]);
      setProjects(pRes.data || []);
      setClients(cRes.data || []);
      setInvites(iRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [q, setQ] = useState("");
  const onboardedProjectIds = new Set(clients.map(c => c.linked_project_id).filter(Boolean));
  const onboardedProjects = useMemo(
    () => projects.filter(p => onboardedProjectIds.has(p.id) && projectMatches(p, q)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, clients, q]
  );
  const pendingProjects = useMemo(
    () => projects.filter(p => !onboardedProjectIds.has(p.id) && projectMatches(p, q)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, clients, q]
  );

  const onboard = async (projectId) => {
    setBusy(projectId);
    try {
      const { data } = await api.post("/crm/onboard-client", { project_id: projectId });
      toast.success(`Onboarded · Temp password: ${data.temp_password}`, { duration: 8000 });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CRM · Onboarding</p>
            <h1 className="font-display text-3xl text-white mt-1">Client Onboarding</h1>
            <p className="text-xs text-gray-500 font-mono mt-1">Trigger the client app invite. MOCKED email + SMS + in-app notification to CRM.</p>
          </div>
          <Link to="/crm/direct-onboard" className="iz-btn-primary flex items-center gap-2" data-testid="cta-direct-onboard">
            <UserPlus className="w-4 h-4"/> Direct Onboard New Client
          </Link>
        </div>
      </div>
      <div className="px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <ProjectSearchBar
            value={q}
            onChange={setQ}
            resultCount={onboardedProjects.length + pendingProjects.length}
            totalCount={projects.length}
            testid="crm-onboarding-search"
          />
          {q.trim() && (onboardedProjects.length + pendingProjects.length) === 0 ? (
            <ProjectSearchEmpty query={q} testid="crm-onboarding-search-empty" />
          ) : (
            <>
              <ProjectsCard title="Onboarded Clients" testid="onboarded-list"
                            rows={onboardedProjects} loading={loading}
                            empty="No onboarded clients yet."
                            onManage={(id) => setManagingId(id)}
                            onOnboardAgain={(id) => onboard(id)}
                            busy={busy}
                            clients={clients}
                            showManage/>
              <ProjectsCard title="Pending / Not Yet Onboarded" testid="pending-list"
                            rows={pendingProjects} loading={loading}
                            empty="Every design project is already onboarded."
                            onManage={(id) => setManagingId(id)}
                            onOnboardAgain={(id) => onboard(id)}
                            busy={busy}
                            clients={clients}/>
            </>
          )}
        </div>

        <div className="iz-card p-5">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Recent invites (MOCKED)</p>
          {invites.length === 0 ? <p className="text-xs text-gray-500 text-center py-6">None yet.</p>
            : <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {invites.slice(0, 15).map(i => (
                <div key={i.id} className="p-3 rounded-md border border-[#262626]" data-testid={`invite-${i.id}`}>
                  <div className="flex items-center gap-2 text-sm text-white"><Mail className="w-3.5 h-3.5 text-[#d4af37]"/>{i.client_email}</div>
                  {i.client_phone && <div className="flex items-center gap-2 text-xs text-gray-400 mt-1"><Phone className="w-3 h-3"/>{i.client_phone}</div>}
                  <div className="text-[10px] text-gray-500 font-mono mt-1">temp pw: <span className="text-[#d4af37]">{i.temp_password}</span></div>
                  <div className="text-[10px] text-gray-600 font-mono mt-1">{new Date(i.sent_at).toLocaleString()}</div>
                </div>
              ))}
            </div>}
        </div>
      </div>

      {managingId && (
        <ManageOnboardedProjectModal
          projectId={managingId}
          onClose={() => setManagingId(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

function ProjectsCard({ title, testid, rows, loading, empty, onManage, onOnboardAgain, busy, clients, showManage }) {
  const teamsMap = useProjectTeams(rows.map(r => r.id));
  return (
    <div className="iz-card overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">{title} · {rows.length}</p>
      </div>
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-[#262626] bg-[#0f0f0f]">
            {["Project", "Client", "Email", "Status", "Action"].map(h =>
              <th key={h} className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 py-3 px-4">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5} className="p-6 text-center text-gray-500">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={5} className="p-6 text-center text-gray-500">{empty}</td></tr>
          ) : rows.map(p => {
            const email = clients.find(c => c.linked_project_id === p.id)?.email || "—";
            const isDirect = !!p.crm_direct_onboarded;
            return (
              <tr key={p.id} className="border-b border-[#1a1a1a]" data-testid={`onboard-row-${p.id}`}>
                <td className="py-3 px-4 text-sm text-white">
                  {p.project_name}
                  {isDirect && (
                    <span className="ml-2 text-[9px] font-mono uppercase tracking-widest text-[#d4af37] border border-[#d4af37]/40 rounded-full px-2 py-0.5">
                      Direct
                    </span>
                  )}
                  <div className="mt-2">
                    <TeamSummaryChips projectId={p.id} slots={teamsMap[p.id]} />
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-gray-300">{p.client_name}</td>
                <td className="py-3 px-4 text-xs font-mono text-gray-500">{email}</td>
                <td className="py-3 px-4">
                  {showManage
                    ? <span className="text-[10px] font-mono uppercase tracking-widest text-[#10b981]">Onboarded</span>
                    : <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Pending</span>}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    {showManage ? (
                      <button onClick={() => onManage(p.id)} className="iz-btn-primary text-xs flex items-center gap-1" data-testid={`manage-${p.id}`}>
                        <Settings className="w-3 h-3"/> Manage
                      </button>
                    ) : (
                      <button onClick={() => onOnboardAgain(p.id)} disabled={busy === p.id} className="iz-btn-primary text-xs flex items-center gap-1" data-testid={`onboard-${p.id}`}>
                        <Send className="w-3 h-3"/> Onboard Client
                      </button>
                    )}
                    <Link
                      to={`/design/projects/${p.id}`}
                      className="iz-btn-secondary text-xs flex items-center gap-1"
                      data-testid={`crm-handoff-${p.id}`}
                      title={p.crm_onboarded_at ? "Review handoff details" : "Complete 5% + 45% handoff to unlock client dashboard"}
                    >
                      <ClipboardCheck className="w-3 h-3"/>
                      {p.crm_onboarded_at ? "Review" : "Handoff"}
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
